import {
    forwardRef,
    useRef,
    useImperativeHandle,
    ReactElement,
    useId
} from 'react';
import styles from './ui-radio-input.module.scss';
import { Link } from 'react-router';
import { UIText } from '../ui-text';
import { UITextButton } from '../..';

export type UIRadioInputProps = {
    label?: string;
    labelButton?: {
        text: string;
        link: string;
    };
    options: { value: string; label: string }[];
    selected?: { value: string; label: string };
    description?: string;
    required?: boolean;
    disabled?: boolean;
    autoComplete?: 'on' | 'off';
    errorText?: string;
    onChange?: (option: { value: string; label: string }) => void;
};

export type UIRadioInputRef = {
    focus: () => void;
};

export const UIRadioInput = forwardRef<UIRadioInputRef, UIRadioInputProps>(
    (props, ref) => {
        const {
            label,
            labelButton,
            options,
            selected,
            description = '',
            required = false,
            disabled = false,
            errorText = '',
            onChange
        } = props;

        const inputRef = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => ({
            focus() {
                inputRef.current?.focus();
            }
        }));

        return (
            <div className={styles.container}>
                {label && (
                    <div className={styles.labelContainer}>
                        <div>
                            <label className={styles.label}>{label}</label>
                            {required ? (
                                <label
                                    className={`${styles.label} ${styles.labelRequired}`}
                                >
                                    {' *'}
                                </label>
                            ) : undefined}
                        </div>

                        {labelButton && (
                            <Link
                                to={labelButton.link}
                                style={{ textDecoration: 'none' }}
                            >
                                <UITextButton variant="primary">
                                    {labelButton.text}
                                </UITextButton>
                            </Link>
                        )}
                    </div>
                )}

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 8
                    }}
                >
                    {options.map((option, index) => (
                        <RadioItem
                            key={index}
                            option={option}
                            checked={option.value === selected?.value}
                            disabled={disabled}
                            onChange={() => {
                                onChange?.(option);
                            }}
                        />
                    ))}
                </div>

                {errorText && (
                    <div className={styles.errorTextContainer}>
                        <UIText variant="small" type="error">
                            {errorText}
                        </UIText>
                    </div>
                )}
                {!errorText && description && (
                    <div className={styles.descriptionTextContainer}>
                        <UIText variant="small">{description}</UIText>
                    </div>
                )}
            </div>
        );
    }
);

type RadioItemProps = {
    option: { value: string; label: string };
    checked: boolean;
    disabled: boolean;
    onChange?: (value: string) => void;
};
function RadioItem(props: RadioItemProps): ReactElement {
    const { option, checked, disabled, onChange } = props;

    const id = useId();

    return (
        <label htmlFor={id} className={styles.radioLabel}>
            <input
                type="radio"
                id={id}
                className={styles.radio}
                value={option.value}
                checked={checked}
                disabled={disabled}
                onChange={(e) => {
                    onChange?.(e.target.value);
                }}
            />
            <span className={styles.customRadio} />
            {option.label}
        </label>
    );
}
